'use client';

import { BarChart2, Table2, type LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

type ReportEmptyStateVariant = 'page' | 'chart' | 'table';

type ReportEmptyStateProps = {
  variant?: ReportEmptyStateVariant;
  className?: string;
  minHeight?: number;
};

type VariantConfig = {
  icon: LucideIcon;
  titleKey: string;
  descriptionKey: string;
  padding: string;
};

const VARIANTS: Record<ReportEmptyStateVariant, VariantConfig> = {
  page: {
    icon: BarChart2,
    titleKey: 'emptyState.title',
    descriptionKey: 'emptyState.description',
    padding: 'px-6 py-16',
  },
  chart: {
    icon: BarChart2,
    titleKey: 'emptyState.chartTitle',
    descriptionKey: 'emptyState.chartDescription',
    padding: 'px-4 py-8',
  },
  table: {
    icon: Table2,
    titleKey: 'emptyState.tableTitle',
    descriptionKey: 'emptyState.tableDescription',
    padding: 'px-4 py-10',
  },
};

const EmptyStateContent = ({ variant }: { variant: ReportEmptyStateVariant }) => {
  const { t } = useTranslation('reports');
  const config = VARIANTS[variant];
  const Icon = config.icon;

  return (
    <div className="flex max-w-md flex-col items-center text-center">
      <div
        className="mb-4 flex h-12 w-12 items-center justify-center rounded-full border border-border/60 bg-background shadow-sm"
        aria-hidden
      >
        <Icon className="h-5 w-5 text-muted-foreground" />
      </div>
      <p className="text-base font-semibold tracking-tight text-foreground">{t(config.titleKey)}</p>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{t(config.descriptionKey)}</p>
    </div>
  );
};

export function ReportEmptyState({ variant = 'page', className, minHeight }: ReportEmptyStateProps) {
  const config = VARIANTS[variant];

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'flex w-full items-center justify-center rounded-xl border border-dashed border-border/70 bg-muted/15',
        config.padding,
        className,
      )}
      style={minHeight ? { minHeight } : undefined}
    >
      <EmptyStateContent variant={variant} />
    </div>
  );
};
